import { get } from 'lodash';
import * as React from 'react';
import { IBookingStateEvent } from '../../../data/entities/booking-state';
import { ICommissionBreakdownObject } from '../../../data/entities/commission-breakdown';
import { ICustomer } from '../../../data/entities/customer';
import { IEventV2 } from '../../../data/entities/event';
import { IBookingBreakdown, IBookingReceipt, IBookingV2 } from '../../../data/entities/event/booking';
import { IProperty } from '../../../data/entities/property';
import { IFXRates } from '../../pages/property/bookings/details/helpers/helpers';
import { Anchor } from '../anchor/Anchor';
import { CustomerServiceEmails } from '../booking-emails/CustomerServiceEmailsContainer';
import { BookingStateEvents } from '../booking-payment/BookingStateEvents';
import { GuestReceipt } from '../booking-payment/GuestReceipt';
import { OwnerPayment } from '../booking-payment/OwnerPayment';
import { Panel } from '../panel/Panel';
import { SectionHeader } from '../section-header/SectionHeader';
import { EventNotes } from './event-notes/EventNotesContainer';
import { FinanceBreakdown } from './finance-breakdown/FinanceBreakdown';
import { valuesConverted } from './helpers';
import { CustomerServiceDetails } from './InternalCustomerServiceDetails';
import { ProfitReport } from './profit-report/ProfitReport';

interface IBookingBreakdownProps {
	event: IEventV2;
	commissionBreakdown: ICommissionBreakdownObject;
	property: IProperty;
	customer: ICustomer;
	loading: boolean;
	fxRates: IFXRates;
	setPending: (pending: boolean) => any;
	setBookingState: (event: IEventV2) => any;
	sendBookingUpdate: () => void;
	bookingStateEvents: IBookingStateEvent[];
}

interface IBookingBreakdownState {
	event: IEventV2;
}

export class InternalBookingDetails extends React.Component<IBookingBreakdownProps, IBookingBreakdownState> {

	public constructor(props: IBookingBreakdownProps) {
		super(props);
		this.state = {
			event: this.props.event
		};
	}

	public componentWillReceiveProps(props: IBookingBreakdownProps) {
		this.setState({ event: props.event });
	}

	public calculateOutstanding(target: 'owner' | 'guest') {
		const booking = this.props.event.booking || {} as IBookingV2;
		const breakdown = booking.value ? booking.value.breakdown : {} as IBookingBreakdown;
		if (target === 'owner') {
			const totalValue = breakdown.owner && breakdown.owner.rent_value || 0;
			const totalPaid = booking.owner_payments.payments.reduce((a, b) => a + (b.amount || 0), 0);
			return totalValue - totalPaid;
		} else {
			const totalValue = booking.value.total || 0;
			const totalPaid = booking.guest_receipts.receipts.reduce((a, b) => a + (b.amount || 0), 0);
			return totalValue - totalPaid;
		}
	}

	public guestReceiptSetReceipts(bookingReceipts: IBookingReceipt[]) {
		const newEvent = { ...this.state.event };
		if (newEvent.booking) {
			newEvent.booking.guest_receipts.receipts = bookingReceipts;
		}
		this.props.setBookingState(newEvent);
	}

	public ownerPaymentSetReceipts(receipts: IBookingReceipt[]) {
		const newEvent = { ...this.state.event };
		if (newEvent.booking) {
			newEvent.booking.owner_payments.payments = receipts;
		}
		this.props.setBookingState(newEvent);
	}

	public render() {
		const booking = this.state.event.booking || {} as IBookingV2;
		const breakdown = booking.value ? booking.value.breakdown : {} as IBookingBreakdown;

		const guestPaymentValue = get(breakdown, 'channel.guest_payment_value') as number;

		const convertedFromGuestPaymentValue = guestPaymentValue / get(breakdown, 'channel.converted_cash_receipt_fx_rate');

		return <div className="booking-details__internal">
			<SectionHeader title="Internal booking breakdown"></SectionHeader>
			{valuesConverted(breakdown) &&
				<Panel withIcon={true} className="fx_conversion__panel">
					This booking has been converted!
				</Panel>}
			<CustomerServiceDetails
				property={this.props.property}
				event={this.props.event}
				setBookingState={this.props.setBookingState}
			/>

			<CustomerServiceEmails
				property={this.props.property}
				customer={this.props.customer}
				event={this.props.event}
			/>
			<Anchor id="breakdown" />
			<FinanceBreakdown
				booking={booking}
				commissionBreakdown={this.props.commissionBreakdown}
			/>

			<ProfitReport
				booking={booking}
				commissionBreakdown={this.props.commissionBreakdown} />

			<Anchor id="notes" />
			<EventNotes setPending={this.props.setPending} event={this.state.event} />

			<Anchor id="receipts" />
			<GuestReceipt
				receipts={booking.guest_receipts.receipts}
				bookingCurrency={breakdown.channel.cash_receipt_currency || booking.value.currency}
				guestPaymentValueInOwnerRentCurrency={guestPaymentValue || 0}
				guestPaymentValueInBookingCurrency={convertedFromGuestPaymentValue || 0}
				// If converted currency doesn't exist then the booking will have been processed with an old calculator id (pre fx calculations)
				// Any booking with no converted currency will be in the same currency as the owner rent as FX bookings will have failed
				// prior to FX
				ownerRentCurrency={breakdown.channel.converted_cash_receipt_currency || booking.value.currency}
				fxRates={this.props.fxRates}
				setReceipts={this.guestReceiptSetReceipts.bind(this)}
			/>

			<Anchor id="payments" />
			<OwnerPayment
				receipts={booking.owner_payments.payments}
				defaultCurrency={booking.value.breakdown.channel.converted_cash_receipt_currency || booking.value.currency}
				totalValue={breakdown.owner && breakdown.owner.payment_value || 0}
				setReceipts={this.ownerPaymentSetReceipts.bind(this)}
				totalPaymentPreCommission={(breakdown.owner && breakdown.owner.payment_value || 0) +
					(breakdown.travelnest && breakdown.travelnest.commission_value || 0)}
				totalCommission={breakdown.travelnest && breakdown.travelnest.commission_value || 0}
			/>

			<Anchor id="booking-state-events" />
			<BookingStateEvents bookingStateEvents={this.props.bookingStateEvents || []} />

		</div>;
	}
	// tslint:disable-next-line:max-file-line-count
}
